import { isAndroid } from "../util/Misc";
import { isChrome } from "./Utils";
import Area from "./Area";
/* eslint-disable no-nested-ternary */
export default class ScrollMonitor {
    static deviceType: string;

    static setDeviceType(deviceType: any): void {
        ScrollMonitor.deviceType = deviceType;
    }

    static getScrollLeft(): number {
        try {
            const clientWin = window.parent;
            const clientDoc = clientWin.document;

            if (clientWin.visualViewport) {
                return clientWin.visualViewport.offsetLeft;
            }

            if (clientWin.pageXOffset != null) {
                return clientWin.pageXOffset;
            }

            if (clientDoc.documentElement != null && (clientDoc.documentElement.scrollLeft != null
                 && clientDoc.documentElement.scrollLeft != 0)) {
                return clientDoc.documentElement.scrollLeft;
            }
            return clientDoc.body.scrollLeft;
        } catch (e) {
            // eslint-disable-line no-empty
        }
        return 0;
    }

    static getScrollTop(): number {
        try {
            const clientWin = window.parent;
            const clientDoc = clientWin.document;

            if (clientWin.visualViewport) {
                return clientWin.visualViewport.offsetTop;
            }

            if (clientWin.pageYOffset != null) {
                return clientWin.pageYOffset;
            }

            if (clientDoc.documentElement != null && (clientDoc.documentElement.scrollTop != null
                && clientDoc.documentElement.scrollTop != 0)) {
                return clientDoc.documentElement.scrollTop;
            }
            return clientDoc.body.scrollTop;
        } catch (e) {
            // eslint-disable-line no-empty
        }
        return 0;
    }

    static getScrollWidth(): number {
        const clientWin = window.parent;
        const clientDoc = clientWin.document;
        let iScrollWidth = 0;

        if (clientWin.innerWidth == null) {
            iScrollWidth = clientDoc.documentElement != null ? clientDoc.documentElement.clientWidth != 0
                ? clientDoc.documentElement.clientWidth : clientDoc.body.clientWidth : clientDoc.body.clientWidth;
        } else {
            let innerWidth = clientWin.innerWidth;

            // If necessary, adjust innerWidth to reflect the presence of the scroll bar.
            innerWidth -= ScrollMonitor.hasRightScrollBar() ? ScrollMonitor.getScrollBarWidth() : 0;
            const clientWidth = clientDoc.body.clientWidth;

            /**
             * on a Tablet we should use innerwidth as the scroll
             * width because as user zooms in innerwidth shrinks
             * and as user zoom out innerwidth expands.
             */
            if (ScrollMonitor.deviceType == "Tablet") {
                iScrollWidth = innerWidth;
            } else {
                iScrollWidth = (clientWidth == 0) ? innerWidth : Math.min(clientWidth, innerWidth);
            }

            if (iScrollWidth == 0) {
                iScrollWidth = clientDoc.body.clientWidth;
            }
        }
        return iScrollWidth;
    }

    static getScrollHeight(): number {
        let iScrollHeight = 0;
        const clientDoc = window.parent.document;

        if (window.parent.innerHeight == null) {
            iScrollHeight = clientDoc.documentElement != null ? clientDoc.documentElement.clientHeight != 0 ? clientDoc.documentElement.clientHeight : clientDoc.body.clientHeight : clientDoc.body.clientHeight;
        } else {
            let innerHeight = window.parent.innerHeight;

            // If necessary, adjust innerHeight to reflect the presence of the scroll bar.
            innerHeight -= ScrollMonitor.hasBottomScrollBar() ? ScrollMonitor.getScrollBarWidth() : 0;

            /*
             * BEWARE browser bugs: clientHeight often reflects a value more like offsetHeight, NOT the correct,
             * visible height of the element. When this happens, it's too BIG, so we apply Math.min below.
             */
            const clientHeight = clientDoc.body.clientHeight;
            const scrollHeight = clientDoc.body.scrollHeight;

            /*
             * We need to make sure the page is long enough to fill the screen. If it's not, then the clientHeight
             * is useless. We detect this by checking that the clientHeight >= scrollHeight.
             */
            iScrollHeight = 0;

            if (clientHeight == 0 || scrollHeight >= clientHeight) { // Ensures that the page is tall enough to fill the window
                iScrollHeight = innerHeight;
            } else {
                iScrollHeight = Math.min(clientHeight, innerHeight);
            }

            if (iScrollHeight == 0) {
                iScrollHeight = clientDoc.body.clientHeight;
            }
        }
        return iScrollHeight;
    }

    static hasRightScrollBar(): boolean {
        const clientHeight = window.parent.document.documentElement.clientHeight;
        const clientWidth = window.parent.document.documentElement.clientWidth;
        const scrollHeight = window.parent.document.documentElement.scrollHeight;
        const innerWidth = window.parent.innerWidth;

        // This means we need to scroll.
        return (clientHeight < scrollHeight) || (clientWidth < innerWidth); // Indicates that a grayed-out scroll bar already appears.
    }

    static hasBottomScrollBar(): boolean {
        const clientWidth = window.parent.document.documentElement.clientWidth;
        const scrollWidth = window.parent.document.documentElement.scrollWidth;
        const clientHeight = window.parent.document.documentElement.clientHeight;
        const innerHeight = window.parent.innerHeight;

        // This means we need to scroll
        return (clientWidth < scrollWidth) || (clientHeight < innerHeight); // Indicates that a grayed-out scroll bar already appears.
    }

    static getScrollBarWidth(): number {
        /** @type {Element} */
        let scr;

        /** @type {Element} */
        let inn;

        /** @type {number} */
        let wNoScroll;

        /** @type {number} */
        let wScroll;

        /*  Outer scrolling div */
        scr = window.parent.document.createElement("div");
        scr.style.position = "absolute";
        scr.style.top = "-1000px";
        scr.style.left = "-1000px";
        scr.style.width = "100px";
        scr.style.height = "50px";

        /*  Start with no scrollbar */
        scr.style.overflow = "hidden";

        /*  Inner content div */
        inn = window.parent.document.createElement("div");
        inn.style.width = "100%";
        inn.style.height = "200px";

        /*  Put the inner div in the scrolling div */
        scr.appendChild(inn);

        /*  Append the scrolling div to the doc */

        window.parent.document.body.appendChild(scr);

        /*  Width of the inner div sans scrollbar */
        wNoScroll = inn.offsetWidth;

        /*  Add the scrollbar */
        scr.style.overflow = "auto";

        /*  Width of the inner div width scrollbar */
        wScroll = inn.offsetWidth;

        /*  Remove the scrolling div from the doc */
        window.parent.document.body.removeChild(window.parent.document.body.lastChild);

        /*  Pixel width of the scroller */
        return wNoScroll - wScroll;
    }

    static getViewport(): Area {
        /** @type {number} */
        const x = ScrollMonitor.getScrollLeft();

        /** @type {number} */
        const y = ScrollMonitor.getScrollTop();

        /** @type {com.inq.utils.Area} */
        const w = window.parent.visualViewport?.width ?? window.parent.innerWidth;

        const viewportHeight = window.parent.visualViewport?.height ?? window.parent.innerHeight;
        const h = parseInt(viewportHeight.toString(), 10);
        const vp = new Area(x, y, w, h);

        return vp;
    }
}
